class GoveeHomeHeroCarousel extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.defaultDuration = 5000;
    this.autoplayEnabled = true;
    this.elapsedBeforePause = 0;
    this.slideStartedAt = 0;
    this.pauseReasons = new Set();
    this.rafId = 0;
    this.wasPlayingBeforeHide = false;
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.pointerStartX = null;
    this.swipeThreshold = 48;

    this.onFrame = this.onFrame.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onHoverStart = this.onHoverStart.bind(this);
    this.onHoverEnd = this.onHoverEnd.bind(this);
    this.onPreviousClick = this.onPreviousClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onProgressClick = this.onProgressClick.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.onEditorBlockSelect = this.onEditorBlockSelect.bind(this);
    this.onEditorBlockDeselect = this.onEditorBlockDeselect.bind(this);
  }

  connectedCallback() {
    this.cacheElements();
    if (!this.slides.length) return;

    this.autoplayEnabled = this.dataset.autoplay !== 'false';
    this.defaultDuration = Number(this.dataset.autoplayDefault) || 5000;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.swipeThreshold = Number(this.dataset.swipeThreshold) || 48;

    this.bindEvents();
    this.goTo(this.currentIndex, { immediate: true });

    if (this.slides.length > 1 && this.autoplayEnabled && !this.reduceMotionQuery.matches) {
      this.startFrameLoop();
    } else {
      this.renderProgress(this.slides.length > 1 ? 0 : 1);
    }
  }

  disconnectedCallback() {
    this.unbindEvents();
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }

  cacheElements() {
    this.slides = Array.from(this.querySelectorAll('[data-govee-hero-slide]'));
    this.progressButtons = Array.from(this.querySelectorAll('[data-govee-hero-progress]'));
    this.progressFills = Array.from(this.querySelectorAll('[data-govee-hero-progress-fill]'));
    this.previousButton = this.querySelector('[data-govee-hero-prev]');
    this.nextButton = this.querySelector('[data-govee-hero-next]');
    this.viewport = this.querySelector('.govee-home-hero__viewport');

    const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.currentIndex = activeIndex >= 0 ? activeIndex : 0;
  }

  bindEvents() {
    this.previousButton?.addEventListener('click', this.onPreviousClick);
    this.nextButton?.addEventListener('click', this.onNextClick);

    for (const [index, button] of this.progressButtons.entries()) {
      button.dataset.slideIndex = String(index);
      button.addEventListener('click', this.onProgressClick);
    }

    if (this.pauseOnHover) {
      const hoverTarget = this.viewport || this;
      hoverTarget.addEventListener('mouseenter', this.onHoverStart);
      hoverTarget.addEventListener('mouseleave', this.onHoverEnd);
    }

    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerCancel);

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('shopify:block:select', this.onEditorBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onEditorBlockDeselect);
  }

  unbindEvents() {
    this.previousButton?.removeEventListener('click', this.onPreviousClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    for (const button of this.progressButtons) {
      button.removeEventListener('click', this.onProgressClick);
    }

    if (this.pauseOnHover) {
      const hoverTarget = this.viewport || this;
      hoverTarget.removeEventListener('mouseenter', this.onHoverStart);
      hoverTarget.removeEventListener('mouseleave', this.onHoverEnd);
    }

    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('pointerup', this.onPointerUp);
    this.removeEventListener('pointercancel', this.onPointerCancel);

    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onEditorBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onEditorBlockDeselect);
  }

  startFrameLoop() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    this.slideStartedAt = performance.now();
    this.elapsedBeforePause = 0;
    this.rafId = requestAnimationFrame(this.onFrame);
  }

  get isPaused() {
    return this.pauseReasons.size > 0;
  }

  get slideCount() {
    return this.slides.length;
  }

  getDuration(index) {
    const duration = Number(this.slides[index]?.dataset.duration);
    return Number.isFinite(duration) && duration > 0 ? duration : this.defaultDuration;
  }

  pause(reason) {
    if (this.pauseReasons.has(reason)) return;

    if (!this.isPaused) {
      this.elapsedBeforePause = performance.now() - this.slideStartedAt;
    }

    this.pauseReasons.add(reason);
  }

  resume(reason) {
    if (!this.pauseReasons.has(reason)) return;

    this.pauseReasons.delete(reason);

    if (!this.isPaused) {
      this.slideStartedAt = performance.now() - this.elapsedBeforePause;
    }
  }

  onFrame(now) {
    if (!this.isConnected || this.slideCount <= 1) return;

    if (!this.autoplayEnabled || this.reduceMotionQuery.matches) {
      this.renderProgress(0);
      this.rafId = 0;
      return;
    }

    const duration = this.getDuration(this.currentIndex);
    const elapsed = this.isPaused ? this.elapsedBeforePause : now - this.slideStartedAt;
    const progress = Math.max(0, Math.min(elapsed / duration, 1));

    this.renderProgress(progress);

    if (!this.isPaused && progress >= 1) {
      this.goTo(this.currentIndex + 1, { immediate: false });
    }

    this.rafId = requestAnimationFrame(this.onFrame);
  }

  onHoverStart() {
    this.pause('hover');
  }

  onHoverEnd() {
    this.resume('hover');
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.wasPlayingBeforeHide = !this.isPaused;
      if (this.wasPlayingBeforeHide) {
        this.pause('visibility');
      }
      return;
    }

    if (this.wasPlayingBeforeHide) {
      this.resume('visibility');
      this.wasPlayingBeforeHide = false;
    }
  }

  onPreviousClick(event) {
    event.preventDefault();
    this.goTo(this.currentIndex - 1);
  }

  onNextClick(event) {
    event.preventDefault();
    this.goTo(this.currentIndex + 1);
  }

  onProgressClick(event) {
    const button = event.currentTarget;
    const index = Number(button?.dataset.slideIndex);
    if (Number.isNaN(index)) return;
    this.goTo(index);
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse') return;
    this.pointerStartX = event.clientX;
  }

  onPointerUp(event) {
    if (this.pointerStartX === null || event.pointerType === 'mouse') return;

    const deltaX = event.clientX - this.pointerStartX;
    this.pointerStartX = null;

    if (Math.abs(deltaX) < this.swipeThreshold) return;
    if (deltaX < 0) {
      this.goTo(this.currentIndex + 1);
    } else {
      this.goTo(this.currentIndex - 1);
    }
  }

  onPointerCancel() {
    this.pointerStartX = null;
  }

  onEditorBlockSelect(event) {
    const blockElement = event.target instanceof HTMLElement ? event.target.closest('[data-govee-hero-slide]') : null;
    if (!blockElement || !this.contains(blockElement)) return;

    const index = this.slides.indexOf(blockElement);
    if (index < 0) return;

    this.goTo(index);
    this.pause('editor');
  }

  onEditorBlockDeselect() {
    this.resume('editor');
  }

  goTo(index, options = {}) {
    if (!this.slideCount) return;

    const normalizedIndex = (index + this.slideCount) % this.slideCount;
    const now = performance.now();

    this.currentIndex = normalizedIndex;
    this.elapsedBeforePause = 0;
    this.slideStartedAt = now;

    for (const [slideIndex, slide] of this.slides.entries()) {
      const isActive = slideIndex === normalizedIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));

      if (isActive) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    }

    for (const [buttonIndex, button] of this.progressButtons.entries()) {
      const isCurrent = buttonIndex === normalizedIndex;
      button.classList.toggle('is-active', isCurrent);
      button.setAttribute('aria-current', isCurrent ? 'true' : 'false');
    }

    this.renderProgress(options.immediate ? 0 : 0);

    if (!this.autoplayEnabled || this.reduceMotionQuery.matches) {
      return;
    }

    if (!this.rafId) {
      this.rafId = requestAnimationFrame(this.onFrame);
    }
  }

  renderProgress(activeProgress) {
    for (const [index, fill] of this.progressFills.entries()) {
      let progress = 0;

      if (index < this.currentIndex) {
        progress = 1;
      } else if (index === this.currentIndex) {
        progress = activeProgress;
      }

      fill.style.width = `${progress * 100}%`;
    }
  }
}

if (!customElements.get('govee-home-hero-carousel')) {
  customElements.define('govee-home-hero-carousel', GoveeHomeHeroCarousel);
}
